/**
 * Platform Owners — super_admin-only panel for adding/revoking owner access
 * without redeploying. Backed by `/api/platform/owners` (DB-backed allowlist
 * with hard-floor founder emails that cannot be removed).
 *
 * Visible only when the current user is `super_admin`. Routes:
 *   GET    /api/platform/owners
 *   POST   /api/platform/owners      { email }
 *   DELETE /api/platform/owners/{email}
 */
import React, { useEffect, useState } from 'react';
import {
  Crown, Plus, Trash2, ShieldCheck, AlertCircle, Loader2, Mail,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../lib/api';
import { Button } from '../../components/ui/button';

export default function PlatformOwnersPanel() {
  const [owners, setOwners] = useState([]);
  const [floorCount, setFloorCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null); // email currently being removed

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/platform/owners`, { withCredentials: true });
      setOwners(res.data.owners || []);
      setFloorCount(res.data.floor_count || 0);
      setError(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to load owners';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async (e) => {
    e?.preventDefault?.();
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address.');
      return;
    }
    try {
      setAdding(true);
      await axios.post(`${API}/platform/owners`, { email }, { withCredentials: true });
      toast.success(`${email} is now a super_admin owner.`);
      setNewEmail('');
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to add owner';
      toast.error(msg);
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (email) => {
    if (!window.confirm(`Revoke super_admin access for ${email}?`)) return;
    try {
      setRemoving(email);
      await axios.delete(`${API}/platform/owners/${encodeURIComponent(email)}`, { withCredentials: true });
      toast.success(`Revoked owner access for ${email}.`);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to revoke owner';
      toast.error(msg);
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="platform-owners-panel">
      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3">
        <Crown className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
        <div className="text-sm">
          <div className="font-semibold text-amber-300">Platform owners (super_admin)</div>
          <p className="text-amber-300/70 mt-1 leading-relaxed">
            Owners have unrestricted access to every workspace, can grant/revoke
            roles, and bypass plan limits. Adding an owner here takes effect
            immediately. {floorCount > 0 && (
              <>The first {floorCount} addresses are <strong>floor owners</strong> —
              pinned in code and unrevokable via the API.</>
            )}
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3 text-sm text-red-300 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      <form
        onSubmit={handleAdd}
        className="rounded-lg border border-border bg-card/40 p-4 space-y-3"
        data-testid="add-owner-form"
      >
        <label className="text-sm font-medium">Add a new owner</label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="email"
              required
              placeholder="owner@yourcompany.co"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              disabled={adding}
              className="w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:border-cyan-500/50 disabled:opacity-50"
              data-testid="new-owner-email-input"
            />
          </div>
          <Button
            type="submit"
            disabled={adding || !newEmail.trim()}
            data-testid="add-owner-submit"
          >
            {adding ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            Grant access
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          The user will get <code className="text-xs bg-muted px-1 rounded">super_admin</code> on
          their next sign-in (existing accounts are pinned immediately).
        </p>
      </form>

      <div className="rounded-lg border border-border overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-muted/40">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Email</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Type</th>
              <th className="text-right px-4 py-3 font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="owners-table-body">
            {loading && (
              <tr><td colSpan="3" className="px-4 py-8 text-center text-muted-foreground">
                <Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading owners…
              </td></tr>
            )}
            {!loading && owners.length === 0 && (
              <tr><td colSpan="3" className="px-4 py-8 text-center text-muted-foreground">
                No owners configured.
              </td></tr>
            )}
            {!loading && owners.map((o) => (
              <tr
                key={o.email}
                className="border-t border-border hover:bg-muted/30"
                data-testid={`owner-row-${o.email}`}
              >
                <td className="px-4 py-3 font-mono text-sm">{o.email}</td>
                <td className="px-4 py-3">
                  {o.is_floor_owner ? (
                    <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300">
                      <ShieldCheck className="w-3 h-3" /> Floor owner
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full border border-cyan-500/40 bg-cyan-500/10 text-cyan-300">
                      <Crown className="w-3 h-3" /> DB owner
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {o.can_remove ? (
                    <button
                      onClick={() => handleRemove(o.email)}
                      disabled={removing === o.email}
                      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-red-500/30 text-red-300 hover:bg-red-500/10 disabled:opacity-50"
                      data-testid={`remove-owner-${o.email}`}
                    >
                      {removing === o.email
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <Trash2 className="w-3.5 h-3.5" />
                      }
                      Revoke
                    </button>
                  ) : (
                    <span className="text-xs text-muted-foreground">Pinned in code</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
